<template>
  <div>
    <HardwareOnlyModal ref="hardwareOnlyModal" />
    <MiniPricingForm v-if="pricingModalIsOpen && !pricingModalComplete" @hide-modal="setPricingModal(false)"/>
    <div class="mb-4">
      <div class="hero-container padding-bottom-2">
        <div class="webstore-hero">
          <h1>{{ headerTitle }}</h1>
        </div>
        <transition name="fade">
          <div
            class="webstore-banner"
            :style="webstoreHero"
            v-if="selectedCategory === 'All'"
          ></div>
        </transition>
      </div>
      <div class="container">
        <div v-if="!isAnonymousCustomer && !smallFleetOpportunity">
          <Notice v-if="!contract.isContractOnFile" noticeType="notice">
            <template v-slot:content>
              {{ webstoreContent.webstoreBody.missing_contract }}
            </template>
          </Notice>
          <Notice v-else-if="hasContractExpired" noticeType="notice">
            <template v-slot:content>
              {{ webstoreContent.webstoreBody.expired_contract }}
            </template>
          </Notice>
          <Notice v-else-if="contract.isContractOnFile && checkoutType === 'ExpressCheckout'" noticeType="notice">
            <template v-slot:content>
              <div>
                {{ webstoreContent.webstoreBody.express_checkout_co_term.text }} 
                <a style="color: #2a6496; text-decoration: underline;" :href="webstoreContent.webstoreBody.express_checkout_co_term.url" target=”_blank”>{{ webstoreContent.webstoreBody.express_checkout_co_term.url_text }}</a>
              </div>
            </template>
          </Notice>
        </div>
        <div class="row">
          <div
            class="col lg-4 md-6 xs-12 sm-9 featured-product"
            v-for="product in productsBySelectedCategory"
            :key="product.key"
            :id="product.key"
          >
            <ProductListing
              @anonymous-modal="openAnonymousModal"
              @inquire-modal="openInquireModal"
              :productFamily="product.family"
              :productKey="product.key"
              :productInfo="productCatalog[product.family][product.key]"
            />
          </div>
        </div>
      </div>
      <v-button
        position="center"
        mobile="8"
        tablet="5"
        desktop="2"
        styling="primary"
        textAlign="center"
        id="checkout-button"
        :disabled="quantityInCart === 0"
        @onClickEvent="trackCheckoutButtonClick()"
      >
        <template v-slot:content>
          <span>
            {{ webstoreContent.proceedToCheckout }}
          </span>
        </template>
      </v-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import ProductListing from "../webstore/product-listing.vue";
import ScrollToTop from "../../../mixins/scrollToTop";
import HardwareOnlyModal from "../webstore/hardware-only-modal.vue";
import MiniPricingForm from './mini-pricing-form.vue';
import vButton from "../../utils/button";
import Notice from "../../utils/notice";
import SalesforceResources from "../../../mixins/salesforce-resources";
import SegmentResources from "../../../mixins/segment-resources";

export default {
  props: ["webstoreProductContent", "ownerEmail"],

  mixins: [
    ScrollToTop,
    SalesforceResources,
    SegmentResources,
  ],

  components: {
    ProductListing,
    HardwareOnlyModal,
    vButton,
    Notice,
    MiniPricingForm
  },

  data() {
    return {
      pageDataFetched: false,
    };
  },
  computed: {
    ...mapGetters([
      "productCatalog",
      "activeProductKey",
      "shoppingCartIsOpen",
      "selectedCategory",
      "isAccountCreatedAfterTitan",
      "sfdcSegments",
      "pricingModalIsOpen",
      "pricingModalComplete",
      "isAnonymousCustomer"

    ]),

    ...mapGetters("content", ["webstoreContent"]),

    ...mapGetters("checkout", [
      "checkoutType",
      "smallFleetOpportunity",
      "accountId",
      "userEmail",
      "source",
      "activePaymentProcessing",
      "isDelinquent",
      "hideLicenses",
      "contract",
      "hasContractExpired",
      "currencyIsoCode",
    ]),

    ...mapGetters("shoppingCart", [
      "productsInCart",
      "couldProductInCartHaveLicenses",
    ]),

    activePage() {
      if (this.activeProductKey === "") {
        return "products";
      } else if (this.productsWithLicenses.includes(this.activeProductKey)) {
        return "addProduct";
      } else {
        return "addAccessory";
      }
    },

    displayLoader() {
      return !this.pageDataFetched;
    },

    webstoreHero: function () {
      return "webstoreHeroImage" in this.webstoreContent
        ? 'background-image: url("' +
            this.webstoreContent["webstoreHeroImage"]["fields"]["file"]["url"] +
            '")'
        : "";
    },

    productsBySelectedCategory() {
                let productsInSelectedCategory = [];
                for (let product of this.webstoreProductContent.productOrder) {
                    let productFromCatalog = this.productCatalog[product.fields.family][product.fields.key]

                    if (productFromCatalog === undefined) {
                        continue
                    }

                    if (this.selectedCategory === 'All' || product.fields.categories.includes(this.selectedCategory)) {
                        if (['VG34', 'CM31', 'CM32', 'AG46', 'AG46P'].includes(product.fields.key)) {
                            continue
                        }
                        productsInSelectedCategory.push(productFromCatalog);
                    }
                }
                return productsInSelectedCategory;
            },

    quantityInCart: function () {
      return this.productsInCart.length > 0
        ? this.productsInCart.reduce((prev, next) => prev + next.quantity, 0)
        : 0;
    },

    headerTitle() {
      return this.hideLicenses && this.selectedCategory === "All"
        ? this.webstoreContent.categoriesInfo[this.selectedCategory]["mm_title"]
        : this.webstoreContent.categoriesInfo[this.selectedCategory]["title"];
    },

    isReturningCustomer() {
      const url = window.location.href;
      return url.includes('/webstore');
    },
  },

  methods: {
    ...mapMutations([
      "setShoppingCartToOpened",
      "setProductCatalog",
      "setCurrencySymbol",
      "setisAccountCreatedAfterTitan",
      "setPricingModal"
    ]),

    ...mapMutations("checkout", [
      "setAccountId",
      "setUserEmail",
      "setSource",
      "setCheckoutType",
      "setCurrencyIsoCode",
      "setWrittenLanguage",
      "setBillingCountry",
      "setActivePaymentProcessing",
      "setIsDelinquent",
      "setIsSelfServiced",
      "setHideLicenses",
      "setContractEndDate",
      "setIsContractOnFile",
      "setSmallFleetOpportunity",
    ]),

    ...mapMutations("billing", ["setCountry"]),

    ...mapActions("content", [
      "getWebstoreContent",
      "getWebstoreProductContent",
      "getFooterContent",
    ]),

    routeToReviewPage() {
      if (this.couldProductInCartHaveLicenses) {
        this.$refs.hardwareOnlyModal.isModalVisible = true;
      } else {
        this.$router.push({ path: "/review-cart" });
      }
    },
    openInquireModal(inquiryObj){
      this.$emit('inquire-modal', inquiryObj);
    },
    openAnonymousModal() {
      this.$emit('anonymous-modal');
    },
    trackCheckoutButtonClick() {
      this.fireSegmentTrackingWithSfdcSegments('Checkout Button Clicked', this.sfdcSegments);
      this.routeToReviewPage();
    },

    trackFaqLinkClick() {
      this.fireSegmentTrackingWithSfdcSegments('FAQ Link Clicked', this.sfdcSegments);
    },

    changeSelectedCategory: function (selectedCategory) {
      this.selectedCategory = selectedCategory;
    },
  },
};
</script>

<style scoped>
.flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.flex-center {
  justify-content: center;
}

#product-catalog {
  width: 100%;
  height: 100%;
  font-family: "Nitti Grotesk", sans-serif;
  font-style: normal;
  font-weight: normal;
  margin: 0 auto;
  padding-bottom: 0px;
}

.webstore-banner {
  position: relative;
  height: 290px;
  color: #fff;
  background-repeat: no-repeat;
  background-position: center;
  background-color: #67666d;
  background-size: cover;
  margin-top: 40px;
}

.hero-container {
  position: relative;
  text-align: center;
  color: white;
}

@media (max-width: 736px) {
  .webstore-hero h1 {
    font-size: 50px;
  }
}

.webstore-hero {
  position: relative;
  height: 125px;
  align-items: center;
  display: flex;
  justify-content: center;
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  background-size: cover;
  margin-top: 30px;
}

.featured-product {
  padding-bottom: 30px;
  margin: 0 -1px; /* Fix column spacing issue on Safari */
}

@media (min-width: 736px) {
  .webstore-hero {
    margin-top: 0;
  }
  .webstore-banner {
    margin-top: 0;
  }

  .featured-product {
    padding-bottom: 80px;
    margin: 0 -1px; /* Fix column spacing issue on Safari */
  }
}

h1,
h2,
h3,
h4,
h5,
p {
  color: #00263e;
  line-height: 1em;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
